import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Megaphone } from 'lucide-react'
import { apiJson } from '../api'
import { hasAnyRole } from '../auth'

interface Announcement {
  id: string
  title: string
  body: string
  category: 'news' | 'announcement'
  pinned: boolean
  expiresAt?: string | null
}

const emptyForm = { title: '', body: '', category: 'announcement', pinned: false, expiresAt: '' }

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold text-slate-500'

export default function AnnouncementForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const location = useLocation()
  const allowManage = hasAnyRole('admin', 'hr')

  const [form, setForm] = useState(emptyForm)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isEdit || !allowManage) return
    const fromState = (location.state as { post?: Announcement } | null)?.post
    const apply = (a: Announcement) => {
      setForm({
        title: a.title,
        body: a.body,
        category: a.category,
        pinned: a.pinned,
        expiresAt: a.expiresAt ?? '',
      })
      setLoading(false)
    }
    if (fromState) {
      apply(fromState)
      return
    }
    apiJson<Announcement>(`/announcements/${id}`)
      .then(apply)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load post')
        setLoading(false)
      })
  }, [id, isEdit, allowManage, location.state])

  const save = async () => {
    setSaving(true)
    setError('')
    const payload: Record<string, unknown> = {
      title: form.title.trim(),
      body: form.body,
      category: form.category,
      pinned: form.pinned,
    }
    if (form.expiresAt) payload.expiresAt = form.expiresAt
    else if (isEdit) payload.expiresAt = ''
    try {
      if (isEdit) {
        await apiJson(`/announcements/${id}`, { method: 'PATCH', body: JSON.stringify(payload), headers: { 'Content-Type': 'application/json' } })
      } else {
        await apiJson('/announcements', { method: 'POST', body: JSON.stringify(payload), headers: { 'Content-Type': 'application/json' } })
      }
      navigate('/intranet')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
      setSaving(false)
    }
  }

  if (!allowManage) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        <AlertCircle size={16} className="shrink-0" />
        Only HR or admin users can publish intranet posts.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Link to="/intranet" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
        <ArrowLeft size={15} /> Back to Intranet
      </Link>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3.5 border-b border-slate-100 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md shadow-brand-500/25">
            <Megaphone size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight">{isEdit ? 'Edit Post' : 'New Post'}</h1>
            <p className="text-xs text-slate-400">Publishing notifies every user via the notification bell</p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
          className="space-y-4 px-6 py-6"
        >
          <div>
            <label className={labelCls}>Title *</label>
            <input required maxLength={200} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Office closed for Thingyan" className={inputCls} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Type</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                <option value="announcement">Announcement</option>
                <option value="news">News</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Expires (optional)</label>
              <input type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} className={inputCls} />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium text-slate-600">
                <input
                  type="checkbox"
                  checked={form.pinned}
                  onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400"
                />
                Pin to top
              </label>
            </div>
          </div>
          <div>
            <label className={labelCls}>Body *</label>
            <textarea
              required
              rows={8}
              maxLength={10000}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Write the full announcement…"
              className={`${inputCls} resize-y`}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <Link
              to="/intranet"
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving || !form.title.trim() || !form.body.trim()}
              className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Publish'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
